// waves-resample (SP2) — pure helper: resample a flat peaks array (0..1, abs)
// to an exact target bar count so each rendered bar maps to one bin. Removes
// the under-sample / pile-up distortion of drawSymRibbon's internal
// `idx = floor((i/barCount) * peaks.length)` when bins != bars (hypothesis 2).
//
// Downsample: max-abs over each source window (preserves transients).
// Upsample: nearest-bin repeat (no interpolation gaps).

export function resampleBins(src: ArrayLike<number>, targetBars: number): number[] {
  if (targetBars <= 0) return [];
  const n = src.length;
  if (n === 0) return [];
  if (n === targetBars) {
    const same = new Array<number>(n);
    for (let i = 0; i < n; i++) same[i] = Math.abs(src[i]);
    return same;
  }
  const out = new Array<number>(targetBars);
  if (targetBars < n) {
    // Downsample: each bar covers a contiguous window of source bins.
    for (let b = 0; b < targetBars; b++) {
      const start = Math.floor((b / targetBars) * n);
      const end = Math.max(start + 1, Math.floor(((b + 1) / targetBars) * n));
      let max = 0;
      for (let i = start; i < end && i < n; i++) {
        const v = Math.abs(src[i]);
        if (v > max) max = v;
      }
      out[b] = max;
    }
  } else {
    // Upsample: nearest source bin.
    for (let b = 0; b < targetBars; b++) {
      const idx = Math.floor((b / targetBars) * n);
      out[b] = Math.abs(src[idx < n ? idx : n - 1]);
    }
  }
  return out;
}

// Headroom-normalize resampled bins so the loudest bar fills `headroom` of the
// lane height. The share manifest stores raw absolute peaks (0..1); a quiet take
// then rendered as a flat sliver because — unlike the desktop overview, which
// auto-gains to ~0.85 — the web never scaled. Mirror that: scale the peak to
// `headroom`, capped at `maxGain` so a near-silent take isn't blown up into
// noise. (B-260621 / web #6 — waveform parity)
export function normalizeBins(bins: number[], headroom = 0.9, maxGain = 8): number[] {
  if (!bins || bins.length === 0) return bins;
  let max = 0;
  for (const v of bins) {
    const a = Math.abs(v);
    if (a > max) max = a;
  }
  if (max < 0.005) return bins; // silence → leave untouched
  const scale = Math.min(maxGain, headroom / max);
  return bins.map((v) => v * scale);
}
